import { AuthService } from './authService';
import {
    PlanMoveRequest,
    PlanMoveResponse,
    ExecuteMoveRequest,
    ExecuteMoveResponse,
    PlanBatchMoveRequest,
    PlanBatchMoveResponse,
    ExecuteBatchMoveRequest,
    ExecuteBatchMoveResponse,
    SuggestDestinationsRequest,
    SuggestDestinationsResponse,
    ActiveMoveBin
} from '../components/warehouse/modules/location-heatmap/heatmapMoveTypes';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class HeatmapMoveService {
    /**
     * Dry-run planning call. Returns candidate items in the source bin,
     * destination info, and any validation errors/warnings.
     */
    async plan(request: PlanMoveRequest): Promise<PlanMoveResponse> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/heatmap-move/plan`,
            request
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(
                errorData?.errorMessage ||
                errorData?.error ||
                `Plan-kall feilet: ${response.status} ${response.statusText}`
            );
        }

        return await response.json();
    }

    /**
     * Execute the move: creates StcIntRelocation + StcReservation + put-away order.
     */
    async execute(request: ExecuteMoveRequest): Promise<ExecuteMoveResponse> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/heatmap-move/execute`,
            request
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(
                errorData?.errorMessage ||
                errorData?.error ||
                `Execute-kall feilet: ${response.status} ${response.statusText}`
            );
        }

        return await response.json();
    }

    /**
     * Dry-run for a batch move (multiple sources, one destination).
     */
    async planBatch(request: PlanBatchMoveRequest): Promise<PlanBatchMoveResponse> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/heatmap-move/plan-batch`,
            request
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(
                errorData?.errorMessage ||
                errorData?.error ||
                `Plan-batch-kall feilet: ${response.status} ${response.statusText}`
            );
        }

        return await response.json();
    }

    /**
     * Execute a batch move: one order with many lines, partial success possible.
     */
    async executeBatch(request: ExecuteBatchMoveRequest): Promise<ExecuteBatchMoveResponse> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/heatmap-move/execute-batch`,
            request
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(
                errorData?.errorMessage ||
                errorData?.error ||
                `Execute-batch-kall feilet: ${response.status} ${response.statusText}`
            );
        }

        return await response.json();
    }

    /**
     * Get bins with pending/in-progress moves (reservations, put-away orders, plan steps).
     * Returns rows that frontend groups by binCode to render one orange ring with tooltip.
     */
    async getActiveMoveBins(warehouseID: string, locationPrefix?: string): Promise<ActiveMoveBin[]> {
        const params = new URLSearchParams();
        params.append('warehouseId', warehouseID);
        if (locationPrefix) params.append('locationPrefix', locationPrefix);

        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/heatmap-move/active-move-bins?${params.toString()}`
        );

        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(data?.error || `Kunne ikke hente aktive flytter: ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Get bin codes that are currently empty (no stock) within an optional location prefix.
     * Fast single-query lookup — used for edit-mode visual indicators.
     */
    async getEmptyBins(warehouseID: string, locationPrefix?: string): Promise<string[]> {
        const params = new URLSearchParams();
        params.append('warehouseId', warehouseID);
        if (locationPrefix) params.append('locationPrefix', locationPrefix);

        const response = await AuthService.authenticatedGet(
            `${API_BASE_URL}/heatmap-move/empty-bins?${params.toString()}`
        );

        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(data?.error || `Kunne ikke hente tomme lokasjoner: ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Suggest destinations for item(s) in a source bin (dry-run of put-away algorithm).
     */
    async suggest(request: SuggestDestinationsRequest): Promise<SuggestDestinationsResponse> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/heatmap-move/suggest`,
            request
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(
                errorData?.errorMessage ||
                errorData?.error ||
                `Suggest-kall feilet: ${response.status} ${response.statusText}`
            );
        }

        return await response.json();
    }
}

export const heatmapMoveService = new HeatmapMoveService();
